import axios, { AxiosInstance } from 'axios'
import { getToken } from './keycloak'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
})

// Attach Keycloak Bearer token to every request
apiClient.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: surface errors cleanly
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired and refresh failed – reload to trigger Keycloak login
      window.location.reload()
    }
    return Promise.reject(error)
  }
)

// ─── Portfolio ──────────────────────────────────────────────────────────────

export const portfolioApi = {
  list: () => apiClient.get('/api/v1/portfolios'),
  get: (id: string) => apiClient.get(`/api/v1/portfolios/${id}`),
  create: (data: unknown) => apiClient.post('/api/v1/portfolios', data),
  summary: (id: string) => apiClient.get(`/api/v1/portfolios/${id}/summary`),
  equityCurve: (id: string) => apiClient.get(`/api/v1/portfolios/${id}/equity-curve`),
}

// ─── Trades ─────────────────────────────────────────────────────────────────

export const tradesApi = {
  list: (params?: Record<string, unknown>) =>
    apiClient.get('/api/v1/trades', { params }),
  get: (id: string) => apiClient.get(`/api/v1/trades/${id}`),
  create: (data: unknown) => apiClient.post('/api/v1/trades', data),
  close: (id: string, data: unknown) =>
    apiClient.post(`/api/v1/trades/${id}/close`, data),
}

// ─── Money Management ────────────────────────────────────────────────────────

export const mmApi = {
  suggest: (data: unknown) => apiClient.post('/api/v1/mm/suggest', data),
  getConfig: () => apiClient.get('/api/v1/mm/config'),
  saveConfig: (data: unknown) => apiClient.put('/api/v1/mm/config', data),
  circuitBreakerStatus: (portfolioId: string) =>
    apiClient.get('/api/v1/mm/circuit-breaker/status', { params: { portfolioId } }),
}

// ─── Export ──────────────────────────────────────────────────────────────────

export const exportApi = {
  startCsv: () => apiClient.post('/api/v1/export/csv'),
  getJob: (id: string) => apiClient.get(`/api/v1/export/jobs/${id}`),
  getDownloadUrl: (id: string) =>
    apiClient.get(`/api/v1/export/jobs/${id}/download`),
}

export default apiClient
